export interface ResizeOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

function resize(
  file: File,
  maxHeight: number,
  maxWidth: number,
  quality: number
) {
  return new Promise<File>((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img

      const widthRatio = maxWidth / width
      const heightRatio = maxHeight / height
      const ratio = Math.min(widthRatio, heightRatio)

      // Only resize if the image is larger than max dimensions
      if (ratio < 1) {
        width = width * ratio
        height = height * ratio
      }

      canvas.width = width
      canvas.height = height

      // Draw the resized image (WebP preserves transparency automatically)
      ctx.drawImage(img, 0, 0, width, height)

      // Convert to WebP blob with compression
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Could not create blob from canvas'))
            return
          }

          // Get file extension and update filename to .webp
          const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
          const webpFileName = `${fileNameWithoutExt}.webp`

          // Create new file as WebP
          const resizedFile = new File([blob], webpFileName, {
            type: 'image/webp'
          })

          resolve(resizedFile)
        },
        'image/webp',
        quality
      )
    }

    img.onerror = () => reject(new Error('Could not load image'))
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Resizes an image file while maintaining aspect ratio and converts to WebP
 * @param file - The image file to resize
 * @param options - Resize options (defaults: maxWidth: 1080, maxHeight: 1080, quality: 0.8)
 * @returns Promise<File> - The resized image file as WebP
 */
export function resizeImage(
  file: File,
  options: ResizeOptions = PRESETS.DEFAULT
): Promise<File> {
  const { maxWidth = 1080, maxHeight = 1080, quality = 0.8 } = options

  // Return original file if not an image
  if (!file.type.startsWith('image/')) {
    return Promise.resolve(file)
  }

  return (
    resize(file, maxHeight, maxWidth, quality)
      .then(file => file)
      // If there is any error we just log it and return the original file
      .catch(errors => {
        console.error(errors)
        return file
      })
  )
}

/**
 * Resize multiple files with progress tracking
 * @param files - Array of files to resize
 * @param options - Resize options
 * @returns Promise<File[]> - Array of resized files as WebP
 */
export async function resizeMultipleImagesWithProgress(
  files: File[],
  options: ResizeOptions = PRESETS.DEFAULT
): Promise<File[]> {
  const promises: Promise<File>[] = []

  for (let i = 0; i < files.length; i++) {
    const resizedFile = resizeImage(files[i], options)
    promises.push(resizedFile)
  }

  return Promise.all(promises)
}

export const PRESETS = {
  DEFAULT: {
    maxWidth: 1080,
    maxHeight: 1080,
    quality: 0.8
  },
  LOGO: {
    maxWidth: 1024,
    maxHeight: 1024,
    quality: 0.9 // Higher quality for logos
  },
  MEDIUM: {
    maxWidth: 720,
    maxHeight: 720,
    quality: 0.8
  },
  SMALL: {
    maxWidth: 150,
    maxHeight: 150,
    quality: 0.7
  },
  ICON: {
    maxWidth: 512,
    maxHeight: 512,
    quality: 0.9 // Higher quality for icons
  },
  THUMBNAIL: {
    maxWidth: 200,
    maxHeight: 200,
    quality: 0.75
  }
}
